/**
 * Getypeerde toegang tot `/tenant-profile`: het type verhuurder en zijn
 * ondernemingsgegevens.
 *
 * Dit gaat over de hele account en niet over één woning. Dat onderscheid staat ook
 * in de teksten op het scherm, want het is precies de verwarring die ontstaat
 * zodra iets naast de instellingen van een residence komt te staan.
 */
import { call, isRecord, readText } from './http'
import type { Failure, Result } from './http'
import type { RequestOptions } from './api-client'

export type TenantFailure = Failure
export type TenantResult<T> = Result<T>

export const TENANT_KINDS = ['private', 'business'] as const
export type TenantKind = (typeof TENANT_KINDS)[number]

export interface TenantProfile {
  readonly id: string
  readonly kind: TenantKind
  readonly legalName: string
  readonly registrationNumber: string
  readonly vatNumber: string
  readonly addressLine1: string
  readonly addressLine2: string
  readonly postalCode: string
  readonly locality: string
  readonly country: string
  readonly effectiveFrom: string
}

export interface TenantProfileState {
  /** Wat er vandaag geldt; null wanneer er nog niets is vastgelegd. */
  readonly current: TenantProfile | null
  /** Alle perioden, nieuwste eerst. */
  readonly history: readonly TenantProfile[]
}

export interface TenantProfileBody {
  readonly kind: TenantKind
  readonly legalName: string
  readonly registrationNumber: string
  readonly vatNumber: string
  readonly addressLine1: string
  readonly postalCode: string
  readonly locality: string
  readonly country: string
  readonly effectiveFrom: string
}

const CALENDAR_DATE = /^\d{4}-\d{2}-\d{2}$/

function readProfile(value: unknown): TenantProfile | null {
  if (!isRecord(value)) {
    return null
  }
  const id = readText(value, 'id')
  const kind = value['kind']
  const effectiveFrom = readText(value, 'effectiveFrom')
  // Het type en de ingangsdatum zijn dragend: ze bepalen welk regime geldt. Een
  // profiel waarvan een van beide niet klopt, wordt overgeslagen in plaats van met
  // een geraden waarde getoond.
  if (
    id === '' ||
    (kind !== 'private' && kind !== 'business') ||
    !CALENDAR_DATE.test(effectiveFrom)
  ) {
    return null
  }

  return {
    id,
    kind,
    effectiveFrom,
    legalName: readText(value, 'legalName'),
    registrationNumber: readText(value, 'registrationNumber'),
    vatNumber: readText(value, 'vatNumber'),
    addressLine1: readText(value, 'addressLine1'),
    addressLine2: readText(value, 'addressLine2'),
    postalCode: readText(value, 'postalCode'),
    locality: readText(value, 'locality'),
    country: readText(value, 'country'),
  }
}

export async function fetchTenantProfile(
  options: RequestOptions = {},
): Promise<TenantResult<TenantProfileState>> {
  return call(
    'GET',
    '/tenant-profile',
    undefined,
    (payload) => {
      if (!isRecord(payload)) {
        return null
      }
      const history: TenantProfile[] = []
      if (Array.isArray(payload['history'])) {
        for (const entry of payload['history']) {
          const profile = readProfile(entry)
          if (profile !== null) {
            history.push(profile)
          }
        }
      }
      return { current: readProfile(payload['current']), history }
    },
    options,
  )
}

/** Een nieuwe periode: een omzetting of een verhuizing. */
export async function createTenantProfile(
  body: TenantProfileBody,
): Promise<TenantResult<TenantProfile>> {
  return call('POST', '/tenant-profile', body, (payload) =>
    isRecord(payload) ? readProfile(payload['profile']) : null,
  )
}

/** Dezelfde periode, rechtgezet. Zie de toelichting bij de API. */
export async function correctTenantProfile(
  id: string,
  body: TenantProfileBody,
): Promise<TenantResult<TenantProfile>> {
  return call('PUT', `/tenant-profile/${encodeURIComponent(id)}`, body, (payload) =>
    isRecord(payload) ? readProfile(payload['profile']) : null,
  )
}

/** Vertaalsleutel bij een mislukte aanroep. */
export function tenantErrorMessageKey(failure: TenantFailure): string {
  switch (failure.code) {
    case 'validation_failed':
      return 'dashboard.company.error.rejected'
    case 'forbidden':
      return 'dashboard.company.error.forbidden'
    case 'conflict':
      return 'dashboard.company.error.conflict'
    default:
      return 'dashboard.company.error.unavailable'
  }
}
